<template>
    <div>

        <PageHeader>
            {{ $t('tipboxWidgetSettings') }}
            <template #side>
                <ButtonIcon :to="parentRoute">
                    {{ $t('goBack') }}
                </ButtonIcon>
            </template>
            <template #subtitle>
                <div>{{ $t('adjustTheAppearanceOfTheTipBoxWidget') }}</div>
            </template>
            <template #bottom>
                <div class="d-flex gap-3">
                    <ButtonIcon
                        icon="file-text"
                        :icon-size="24"
                        variant="link"
                        class="p-0"
                        @click="openHelp"
                    >
                        {{ $t('takeALookAtHelp') }}
                    </ButtonIcon>
                </div>
            </template>
        </PageHeader>

        <PageLoading v-if="requestSpace.getLoading()" />

        <ErrorMessage v-else-if="requestSpace.getError()" :value="requestSpace.getError()" :dismissible="false" />

        <template v-else-if="space">

            <b-overlay :show="loading" rounded="sm" class="card flex-grow-1 flex-basis-0 width-min-300">
                <Card :header="$t('tipboxWidget')" class="mb-0 shadow-none">
                    <ul class="mb-0">
                        <li>
                            <strong>{{ $t('identifier') }}</strong>:
                            <span v-if="tipbox">{{ tipbox.id || '' }}</span>
                        </li>
                        <li>
                            <strong>{{ $t('space') }}</strong>:
                            <span v-if="space">{{ space.url || '' }}</span>
                        </li>
                    </ul>
                </Card>
            </b-overlay>

            <div class="d-flex gap-4 flex-wrap mb-4">

                <b-overlay :show="loading" rounded="sm" class="card flex-grow-1 flex-basis-0 mb-0 width-min-300">
                    <Card :header="`1. ${$t('customAppearance')}`" header-id="tb-Widget-header-1" class="mb-0 shadow-none">

                        <div class="d-flex flex-column">
                            <div class="d-flex flex-wrap gap-2">
                                <FormValidator
                                    id="form-group-header-color"
                                    label-id="tb-Widget-label-1"
                                    :validator="$v.form.headerColor"
                                    v-slot="slotProps"
                                    form-group
                                    :label="$t('tipboxForm.label.headerColor')"
                                    class="flex-grow-1 flex-basis-0 width-min-200"
                                >
                                    <ColorField
                                        id="form-header-color"
                                        v-model="form.headerColor"
                                        :placeholder="tipboxDefaults.headerColor"
                                        :state="slotProps.state"
                                        class="mb-0"
                                        :disabled="busy"
                                    />
                                </FormValidator>
                                <FormValidator
                                    id="form-group-text-color"
                                    label-id="tb-Widget-label-2"
                                    :validator="$v.form.textColor"
                                    v-slot="slotProps"
                                    form-group
                                    :label="$t('tipboxForm.label.textColor')"
                                    class="flex-grow-1 flex-basis-0 width-min-200"
                                >
                                    <ColorField
                                        id="form-text-color"
                                        v-model="form.textColor"
                                        :placeholder="tipboxDefaults.textColor"
                                        :state="slotProps.state"
                                        class="mb-0"
                                        :disabled="busy"
                                    />
                                </FormValidator>
                            </div>
                            <div class="d-flex flex-wrap gap-2">
                                <FormValidator
                                    id="form-group-button-color"
                                    label-id="tb-Widget-label-3"
                                    :validator="$v.form.buttonColor"
                                    v-slot="slotProps"
                                    form-group
                                    :label="$t('tipboxForm.label.buttonColor')"
                                    class="flex-grow-1 flex-basis-0 width-min-200"
                                >
                                    <ColorField
                                        id="form-button-color"
                                        v-model="form.buttonColor"
                                        :placeholder="tipboxDefaults.buttonColor"
                                        :state="slotProps.state"
                                        class="mb-0"
                                        :disabled="busy"
                                    />
                                </FormValidator>
                                <FormValidator
                                    id="form-group-button-text-color"
                                    label-id="tb-Widget-label-4"
                                    :validator="$v.form.buttonTextColor"
                                    v-slot="slotProps"
                                    form-group
                                    :label="$t('tipboxForm.label.buttonTextColor')"
                                    class="flex-grow-1 flex-basis-0 width-min-200"
                                >
                                    <ColorField
                                        id="form-button-text-color"
                                        v-model="form.buttonTextColor"
                                        :placeholder="tipboxDefaults.buttonTextColor"
                                        :state="slotProps.state"
                                        class="mb-0"
                                        :disabled="busy"
                                    />
                                </FormValidator>
                            </div>
                            <div class="d-flex flex-wrap gap-2">
                                <FormValidator
                                    id="form-group-frame-color"
                                    label-id="tb-Widget-label-6"
                                    :validator="$v.form.frameColor"
                                    v-slot="slotProps"
                                    form-group
                                    :label="$t('tipboxForm.label.frameColor')"
                                    class="flex-grow-1 flex-basis-0 width-min-200"
                                >
                                    <ColorField
                                        id="form-frame-color"
                                        v-model="form.frameColor"
                                        :placeholder="tipboxDefaults.frameColor"
                                        :state="slotProps.state"
                                        class="mb-0"
                                        :disabled="busy"
                                    />
                                </FormValidator>
                                <FormValidator
                                    id="form-group-background-color"
                                    label-id="tb-Widget-label-7"
                                    :validator="$v.form.backgroundColor"
                                    v-slot="slotProps"
                                    form-group
                                    :label="$t('tipboxForm.label.backgroundColor')"
                                    class="flex-grow-1 flex-basis-0 width-min-200"
                                >
                                    <ColorField
                                        id="form-background-color"
                                        v-model="form.backgroundColor"
                                        :placeholder="tipboxDefaults.backgroundColor"
                                        :state="slotProps.state"
                                        class="mb-0"
                                        :disabled="busy"
                                    />
                                </FormValidator>
                            </div>
                        </div>

                        <div class="w-100 p-3"></div>

                        <FormValidator
                            id="form-group-avatar"
                            label-id="tb-Widget-label8"
                            class="d-flex flex-column flex-grow-1"
                            :validator="$v.formAvatar.avatar"
                            form-group
                            :label="$t('tipboxForm.label.avatar')"
                            v-slot="slotProps"
                        >
                            <div class="d-flex gap-2">
                                <FormFile
                                    id="form-avatar"
                                    v-model="formAvatar.avatar"
                                    accept="image/*"
                                    show-thumbnails
                                    :state="slotProps.state"
                                    :disabled="busy"
                                    :loading="requestAvatar.getLoading()"
                                />
                                <b-modal ref="formAvatarModal" centered hide-footer hide-header body-class="text-center d-flex justify-content-center align-items-center height-min-200">
                                    <ImageLoader v-if="tipbox" :src="tipbox.avatar" image-class="w-auto" error-class="w-auto" style="max-width: 100%;" />
                                </b-modal>
                                <ImageLoader
                                    v-if="tipbox"
                                    :src="tipbox.avatar"
                                    class="my-auto cursor-pointer"
                                    height="40px"
                                    width="40px"
                                    @click.native="$refs.formAvatarModal.show()"
                                />
                            </div>
                        </FormValidator>

                        <div class="w-100 p-3"></div>

                        <FormValidator id="form-group-header" label-id="tb-Widget-label9" :validator="$v.form.header" :msg-props="{ max: $v.form.header.$params.maxLength.max }" form-group>
                            <template #label>
                                <div class="d-flex align-items-center">
                                    <div><span id="tb-Widget-label9">{{ $t('tipboxForm.label.header') }}</span></div>
                                    <div class="ml-auto text-secondary font-weight-normal">{{ form.header.length }}/{{ $v.form.header.$params.maxLength.max }}</div>
                                </div>
                            </template>
                            <template #default="slotProps">
                                <b-form-input
                                    id="form-header"
                                    v-model="form.header"
                                    :state="slotProps.state"
                                    :placeholder="tipboxDefaults.header"
                                    class=""
                                    :disabled="busy"
                                />
                            </template>
                        </FormValidator>
                        <FormValidator id="form-group-description" label-id="tb-Widget-label10" :validator="$v.form.description" :msg-props="{ max: $v.form.description.$params.maxLength.max }" form-group>
                            <template #label>
                                <div class="d-flex align-items-center">
                                    <div><span id="tb-Widget-label10">{{ $t('tipboxForm.label.description') }}</span></div>
                                    <div class="ml-auto text-secondary font-weight-normal">{{ form.description.length }}/{{ $v.form.description.$params.maxLength.max }}</div>
                                </div>
                            </template>
                            <template #default="slotProps">
                                <b-form-textarea
                                    id="form-description"
                                    v-model="form.description"
                                    :state="slotProps.state"
                                    :placeholder="tipboxDefaults.description"
                                    class=""
                                    :disabled="busy"
                                    rows="4"
                                />
                            </template>
                        </FormValidator>
                        <FormValidator id="form-group-thanks" label-id="tb-Widget-label11" :validator="$v.form.thanks" :msg-props="{ max: $v.form.thanks.$params.maxLength.max }" form-group>
                            <template #label>
                                <div class="d-flex align-items-center">
                                    <div><span id="tb-Widget-label11">{{ $t('tipboxForm.label.thanks') }}</span></div>
                                    <div class="ml-auto text-secondary font-weight-normal">{{ form.thanks.length }}/{{ $v.form.thanks.$params.maxLength.max }}</div>
                                </div>
                            </template>
                            <template #default="slotProps">
                                <b-form-input
                                    id="form-thanks"
                                    v-model="form.thanks"
                                    :state="slotProps.state"
                                    :placeholder="tipboxDefaults.thanks"
                                    class=""
                                    :disabled="busy"
                                />
                            </template>
                        </FormValidator>

                        <div class="w-100 p-3"></div>

                        <b-form-group :label="$t('tipboxForm.label.amounts')" label-id="tb-Widget-label12">
                            <div class="d-flex flex-wrap gap-2">
                                <FormValidator id="form-group-amounts-0" :validator="$v.form.amounts[0]" :msg-props="{ min: minAmount[0] }" v-slot="slotProps" class="flex-grow-1 flex-basis-0 width-min-50 width-max-200">
                                    <b-form-input id="form-amounts-0" v-model="form.amounts[0]" :placeholder="tipboxDefaults.amounts[0].toString()" :state="slotProps.state" :disabled="busy" />
                                </FormValidator>
                                <FormValidator id="form-group-amounts-1" :validator="$v.form.amounts[1]" :msg-props="{ min: minAmount[1] }" v-slot="slotProps" class="flex-grow-1 flex-basis-0 width-min-50 width-max-200">
                                    <b-form-input id="form-amounts-1" v-model="form.amounts[1]" :placeholder="tipboxDefaults.amounts[1].toString()" :state="slotProps.state" :disabled="busy" />
                                </FormValidator>
                                <FormValidator id="form-group-amounts-2" :validator="$v.form.amounts[2]" :msg-props="{ min: minAmount[2] }" v-slot="slotProps" class="flex-grow-1 flex-basis-0 width-min-50 width-max-200">
                                    <b-form-input id="form-amounts-2" v-model="form.amounts[2]" :placeholder="tipboxDefaults.amounts[2].toString()" :state="slotProps.state" :disabled="busy" />
                                </FormValidator>
                            </div>
                        </b-form-group>

                        <div class="w-100 p-3"></div>

                        <b-form-group id="form-group-show-icon" :label="$t('tipboxForm.label.iconOnButtons')" label-id="tb-Widget-label13">
                            <div class="d-flex flex-column gap-3">
                                <b-form-checkbox id="form-show-icon" v-model="showIcon">{{ $t('tipboxForm.label.showIcons') }}</b-form-checkbox>
                            </div>
                        </b-form-group>
                        <b-form-group v-if="showIcon" id="form-group-icon" :label="$t('tipboxForm.label.icon')">
                            <div class="d-flex gap-3">
                                <b-form-select
                                    id="form-icon"
                                    v-model="form.icon"
                                    :options="optionsIcons"
                                    text-field="label"
                                    class="flex-grow-1"
                                    :placeholder="$t('select')"
                                    add-null-option
                                />
                                <div class="width-40 flex-shrink-0">
                                    <ImageLoader v-if="form.icon && form.iconVersion" :src="getIconPath()" :size="40" />
                                </div>
                            </div>
                        </b-form-group>
                        <b-form-group v-if="showIcon" id="form-group-icon-version" :label="$t('tipboxForm.label.type')">
                            <div class="d-flex gap-3">
                                <b-form-select
                                    id="form-icon-version"
                                    v-model="form.iconVersion"
                                    :options="optionsIconVersions"
                                    text-field="label"
                                    class="flex-grow-1"
                                    :placeholder="$t('select')"
                                    add-null-option
                                />
                                <div class="width-40 flex-shrink-0"></div>
                            </div>
                        </b-form-group>

                        <div class="w-100 p-3"></div>

                        <FormValidator id="form-group-source-url" :label="$t('tipboxForm.label.webhook')" :validator="$v.form.webhook" v-slot="slotProps" form-group>
                            <div class="d-flex gap-3">
                                <b-form-input
                                    id="form-icon-version"
                                    v-model="form.webhook"
                                    :state="slotProps.state"
                                    placeholder="https://"
                                    class=""
                                    :disabled="busy"
                                />
                                <div class="width-40 flex-shrink-0"></div>
                            </div>
                        </FormValidator>
                        <b-form-group class="mb-0">
                            <ButtonIcon :disabled="busy" :loading="busy" @click="formSubmit()">
                                {{ $t('save') }}
                            </ButtonIcon>
                        </b-form-group>
                    </Card>
                </b-overlay>

                <b-overlay :show="loading" rounded="sm" class="card flex-grow-1 flex-basis-0 mb-0 width-min-300">
                    <Card no-padding class="mb-0 overflow-hidden shadow-none">
                        <img src="@/assets/images/tipbox-1.png" class="w-100 mb-4">
                        <img src="@/assets/images/tipbox-2.png" class="w-100 mb-2">
                    </Card>
                    <Card class="mb-4 shadow-none w-100 h-50">
                        <TipboxPreview v-if="tipbox && tipbox.id" :config="tipboxConfig" />
                    </Card>
                </b-overlay>

                <b-overlay :show="loading" rounded="sm" class="w-100">
                    <Card :header="`2. ${$t('insertOnPage')}`" header-id="tb-Widget-header-2" class="w-100 width-min-300">
                        <div class="my-3">
                            <ButtonIcon
                                variant="link"
                                class="expandable p-0"
                                :class="{ 'collapsed': !showParameters }"
                                icon="chevron-up"
                                flip-icon
                                @click="showParameters = !showParameters"
                            >
                                {{ $t('addYourOwnParameters') }}
                            </ButtonIcon>
                            <b-collapse v-model="showParameters">
                                <div class="parameters">
                                    <div class="my-2"></div>
                                    <b-form-group id="form-group-abpar1" :label="$t('abpar1')" label-cols-sm="3">
                                        <b-form-input id="form-abpar1" v-model="parameters.abpar1" />
                                    </b-form-group>
                                    <b-form-group id="form-group-abpar2" :label="$t('abpar2')" label-cols-sm="3">
                                        <b-form-input id="form-abpar2" v-model="parameters.abpar2" />
                                    </b-form-group>
                                    <b-form-group id="form-group-abpar3" :label="$t('abpar3')" label-cols-sm="3">
                                        <b-form-input id="form-abpar3" v-model="parameters.abpar3" />
                                    </b-form-group>
                                </div>
                            </b-collapse>
                        </div>

                        <b-card-text>
                            {{ $t('copyTheCodeAndInsertItIntoYourPage') }}
                        </b-card-text>
                        <output class="form-control h-auto my-3">
                            <pre class="mb-0"><code id="generated-code">{{ tipboxCode }}</code></pre>
                        </output>
                        <ButtonIcon icon="copy" @click="copyTipboxCodeAuto()">
                            {{ $t('generator.copyCode') }}
                        </ButtonIcon>
                    </Card>
                </b-overlay>

            </div>

        </template>

    </div>
</template>



<script>
    import { serialize as objectToFormData } from 'object-to-formdata';
    import pick from 'lodash/pick';
    import merge from 'lodash/merge';
    import cloneDeep from 'lodash/cloneDeep';
    import { required, decimal, minValue, helpers, maxLength } from 'vuelidate/lib/validators';
    import { image, url } from '@/utils/validators';
    import { generateId, deepMergeWithFilter, scrollToElement } from '@/utils/util';
    import validationService from '@/services/validationService';
    import ColorField from '@/components/common/ColorField.vue';
    import FormValidator from '@/components/common/FormValidator.vue';
    import TipboxPreview from '@/components/common/TipboxPreview.vue';
    import PageLoading from '@/components/common/PageLoading.vue';
    import FormFile from '@/components/common/FormFile.vue';
    import ImageLoader from '@/components/common/ImageLoader.vue';

    const FORM_DEFAULT_DATA = Object.freeze({
        amounts: [null, null, null],
        header: '',
        description: '',
        thanks: '',
        headerColor: null,
        textColor: null,
        buttonColor: null,
        buttonTextColor: null,
        frameColor: null,
        backgroundColor: null,
        icon: null,
        iconVersion: null,
        webhook: '',
    });

    const FORM_AVATAR_DEFAULT_DATA = Object.freeze({
        avatar: null,
    });

    export default {
        name: 'TipboxSettings',
        components: {
            ColorField,
            FormValidator,
            TipboxPreview,
            PageLoading,
            FormFile,
            ImageLoader,
        },
        data() {
            return {
                id: Number.parseInt(this.$route.params.id),
                tipboxDefaults: this.$t('tipboxForm.placeholder'),
                form: cloneDeep(FORM_DEFAULT_DATA),
                formAvatar: cloneDeep(FORM_AVATAR_DEFAULT_DATA),
                tipboxScriptUrl: process.env.VUE_APP_WIDGET_SCRIPT_URL,
                tipboxId: generateId(),
                requestSpace: this.$req('space/Space'),
                requestIcons: this.$req('tipbox/Icons'),
                requestIconVersions: this.$req('tipbox/IconVersions'),
                requestAvatar: this.$req('tipbox/SpaceAvatar'),
                showIcon: false,
                tipbox: {},
                showParameters: false,
                parameters: {},
            };
        },
        validations() {
            return {
                form: {
                    headerColor: { hexColor: validationService.hexColor },
                    textColor: { hexColor: validationService.hexColor },
                    buttonColor: { hexColor: validationService.hexColor },
                    buttonTextColor: { hexColor: validationService.hexColor },
                    frameColor: { hexColor: validationService.hexColor },
                    backgroundColor: { hexColor: validationService.hexColor },
                    amounts: {
                        0: { required, decimal, minValue: minValue(this.minAmount[0]) },
                        1: { required, decimal, minValue: minValue(this.minAmount[1]) },
                        2: { required, decimal, minValue: minValue(this.minAmount[2]) },
                    },
                    header: { maxLength: maxLength(60) },
                    description: { maxLength: maxLength(300) },
                    thanks: { maxLength: maxLength(60) },
                    url: { url: val => !val || url(val) },
                },
                formAvatar: {
                    avatar: { image: value => !helpers.req(value) || image(value) },
                },
            };
        },
        computed: {
            parentRoute() {
                return this.$route.params.parentUrl ? this.$router.resolve(this.$route.params.parentUrl).resolved : this.$route.matched.slice(-2)[0];
            },
            space() {
                return this.requestSpace.getData();
            },
            minAmount() {
                const min = 5;
                return {
                    0: min,
                    1: Math.max(min + 0.5, (Number.parseInt(this.form.amounts[0]) || 0) + 0.5),
                    2: Math.max(min + 1, (Number.parseInt(this.form.amounts[1]) || 0) + 0.5),
                };
            },
            apiParams() {
                return {
                    requestParams: this.form,
                    urlParams: { spaceId: this.space?.id },
                };
            },
            loading() {
                return this.$store.getters['tipbox/getLoading']('tipbox') || this.$store.getters['tipbox/getLoading']('defaults');
            },
            busy() {
                return this.$store.getters['tipbox/getBusy']('tipbox');
            },
            error() {
                return this.$store.getters['tipbox/getError']('tipbox');
            },
            tipboxConfig() {
                if (!this.tipbox) return {};
                const attrs = {
                    'data-bb-space-id': this.tipbox.id,
                    'bb-preview': 'true',
                };
                /* eslint-disable prettier-vue/prettier */
                if (this.form.headerColor)                    attrs['data-bb-header-color'] = this.form.headerColor || this.tipboxDefaults.headerColor;
                if (this.form.textColor)                      attrs['data-bb-text-color'] = this.form.textColor || this.tipboxDefaults.textColor;
                if (this.form.buttonColor)                    attrs['data-bb-button-background-color'] = this.form.buttonColor || this.tipboxDefaults.buttonColor;
                if (this.form.buttonTextColor)                attrs['data-bb-button-text-color'] = this.form.buttonTextColor || this.tipboxDefaults.buttonTextColor;
                if (this.form.frameColor)                     attrs['data-bb-frame-color'] = this.form.frameColor || this.tipboxDefaults.frameColor;
                if (this.form.backgroundColor)                attrs['data-bb-background-color'] = this.form.backgroundColor || this.tipboxDefaults.backgroundColor;
                if (this.form.header)                         attrs['data-bb-header-label'] = this.form.header || this.tipboxDefaults.header;
                if (this.form.description)                    attrs['data-bb-description-label'] = this.form.description || this.tipboxDefaults.description;
                if (this.form.thanks)                         attrs['data-bb-thanks-label'] = this.form.thanks || this.tipboxDefaults.thanks;
                if (this.form.amounts[0])                     attrs['data-bb-amounts-1'] = this.form.amounts[0] || this.tipboxDefaults.amounts[0];
                if (this.form.amounts[1])                     attrs['data-bb-amounts-2'] = this.form.amounts[1] || this.tipboxDefaults.amounts[1];
                if (this.form.amounts[2])                     attrs['data-bb-amounts-3'] = this.form.amounts[2] || this.tipboxDefaults.amounts[2];
                if (this.form.icon || this.showIcon)          attrs['data-bb-icon'] = this.form.icon;
                if (this.form.iconVersion || this.showIcon)   attrs['data-bb-icon-version'] = this.form.iconVersion;
                if (this.tipbox.avatar)                       attrs['data-bb-avatar'] = this.tipbox.avatar;
                /* eslint-enable prettier-vue/prettier */
                return attrs;
            },
            tipboxCode() {
                // eslint-disable-next-line prettier-vue/prettier
                const attrs = [
                    `id="tipbox-${this.tipboxId}"`,
                    'class="bb-widget"',
                    'data-bb-type="tipbox"',
                    `data-bb-space-id="${this.tipbox.id}"`,
                ];

                if (this.parameters.abpar1) attrs.push(`data-bb-p1="${this.parameters.abpar1}"`);
                if (this.parameters.abpar2) attrs.push(`data-bb-p2="${this.parameters.abpar2}"`);
                if (this.parameters.abpar3) attrs.push(`data-bb-p3="${this.parameters.abpar3}"`);
                const attrsStr = attrs.join(' ');
                const scriptTag = `<script src="${this.tipboxScriptUrl}"><\/script>`; // eslint-disable-line no-useless-escape
                const tipboxTag = `<div ${attrsStr}></div>`;
                return `${scriptTag}\n${tipboxTag}`;
            },
            optionsIcons() {
                return (this.requestIcons.getData() || []).sort((a, b) => a.label > b.label);
            },
            optionsIconVersions() {
                return this.requestIconVersions.getData() || [];
            },
        },
        watch: {
            showIcon() {
                if (!this.showIcon) {
                    this.form.icon = null;
                    this.form.iconVersion = null;
                }
            },
        },
        methods: {
            openHelp() {
                window.Gleap.openHelpCenterArticle('25', false);
            },
            getIconPath() {
                return require(`@/assets/ikonki_tipbox/${this.form.icon}/${this.form.iconVersion}.svg`); // eslint-disable-line
            },
            fetchIcons() {
                return this.requestIcons.get().catch(() => {});
            },
            fetchIconVersions() {
                return this.requestIconVersions.get().catch(() => {});
            },
            fetchSpace() {
                return this.requestSpace.get(this.id).catch(() => {});
            },
            initFormData() {
                this.form = deepMergeWithFilter(FORM_DEFAULT_DATA, this.tipbox);
                if (this.form.icon || this.form.iconVersion) this.showIcon = true;
            },
            initFormAvatarData() {
                this.formAvatar = deepMergeWithFilter(FORM_AVATAR_DEFAULT_DATA, {});
            },
            fetchTipboxDefaults() {
                return this.$store
                    .dispatch('tipbox/fetchDefaults')
                    .then(data => {
                        this.tipboxDefaults = merge({}, this.tipboxDefaults, data);
                        this.initFormData();
                    })
                    .catch(error => this.$toastr.error(error.message, this.$t('errorOccurred')));
            },
            fetchTipbox() {
                return this.$store
                    .dispatch('tipbox/fetchTipbox', { urlParams: this.apiParams.urlParams })
                    .then(data => {
                        this.tipbox = { ...this.tipbox, ...data };
                        this.initFormData();
                    })
                    .catch(error => this.$toastr.error(error.message, this.$t('errorOccurred')));
            },
            copyingConfirmation() {
                this.$toastr.success(this.$t('generator.copied'));
            },
            copyTipboxCodeAuto() {
                if (!this.tipboxCode) return;
                this.$copyText(this.tipboxCode)
                    .then(this.copyingConfirmation)
                    .catch(() => {});
            },
            copyTipboxCodeAutoManual(e) {
                if (!this.tipboxCode) return;
                if (e.target.id !== 'generated-code') return;
                const removeSpaces = string => string?.trim().replace(/(?:\r\n|\r|\n)/g, '');
                const selection = document.getSelection();
                if (removeSpaces(selection.toString()) === removeSpaces(this.tipboxCode)) this.copyingConfirmation();
            },
            clearStoreData() {
                this.$store.commit('tipbox/clearTipbox');
            },
            async formSubmit() {
                this.$v.form.$touch();
                this.$v.formAvatar.$touch();
                if (this.$v.form.$invalid || this.$v.formAvatar.$invalid) {
                    await this.$nextTick();
                    scrollToElement('.is-invalid', { offset: -80 });
                    return;
                }
                await this.saveForm();
                await this.saveAvatar();
            },
            saveForm() {
                return this.$store
                    .dispatch('tipbox/patchTipbox', this.apiParams)
                    .then(data => {
                        this.tipbox = { ...this.tipbox, ...data };
                        this.$toastr.success(this.$t('dataSavedSuccessfully'));
                        this.$v.form.$reset();
                    })
                    .catch(error => this.$toastr.error(error.message, this.$t('errorOccurred')));
            },
            saveAvatar() {
                if (!this.formAvatar.avatar) {
                    return Promise.resolve();
                }
                const apiParams = {
                    urlParams: { id: this.space?.id },
                    requestParams: objectToFormData(pick(this.formAvatar, ['avatar'])),
                    headers: { 'Content-Type': 'multipart/form-data' },
                };
                return this.requestAvatar
                    .post(apiParams)
                    .then(({ avatar }) => {
                        if (avatar) this.$set(this.tipbox, 'avatar', avatar);
                        this.initFormAvatarData();
                        this.$v.formAvatar.$reset();
                    })
                    .catch(error => this.$toastr.error(error.message, this.$t('errorOccurred')));
            },
        },
        async created() {
            this.clearStoreData();
            await this.fetchSpace();
            this.fetchTipbox();
            this.fetchTipboxDefaults();
            this.fetchIcons();
            this.fetchIconVersions();
        },
        mounted() {
            document.addEventListener('copy', this.copyTipboxCodeAutoManual);
        },
        beforeDestroy() {
            document.removeEventListener('copy', this.copyTipboxCodeAutoManual);
            this.clearStoreData();
        },
    };
</script>

